import { useEffect, useMemo, useState } from 'react';
import {
  clampPageNumber,
  getUsablePageHeight,
  REPORT_PAGE_METRICS,
} from './reportPageMetrics';

const getElementContentHeight = (element) => {
  if (!element) {
    return 0;
  }
  return Math.max(
    element.scrollHeight || 0,
    element.clientHeight || 0,
    element.getBoundingClientRect?.().height || 0
  );
};

const isPagedMode = (mode) => mode === 'strict' || mode === 'guides' || mode === 'paged_guides';

const getPageCountFromHeight = (contentHeight, metrics) => {
  const usablePageHeight = getUsablePageHeight(metrics);
  const totalHeight = Math.max(
    1,
    contentHeight + metrics.contentTopInsetPx + metrics.contentBottomInsetPx
  );
  const pagedStride = usablePageHeight + metrics.sheetGapPx;
  return Math.max(1, Math.ceil((totalHeight + metrics.sheetGapPx) / pagedStride));
};

const getCurrentPageFromViewport = (element, metrics, pageCount) => {
  if (!element || typeof window === 'undefined') {
    return 1;
  }
  const usablePageHeight = getUsablePageHeight(metrics);
  const pagedStride = usablePageHeight + metrics.sheetGapPx;
  const rect = element.getBoundingClientRect();
  const viewportAnchor = window.innerHeight * 0.44;
  const contentAnchor = viewportAnchor - rect.top + metrics.contentTopInsetPx;
  const projected = Math.floor(contentAnchor / pagedStride) + 1;
  return clampPageNumber(projected, pageCount);
};

const isScrollableContainer = (element) => {
  if (!element || typeof window === 'undefined') {
    return false;
  }
  const style = window.getComputedStyle(element);
  const overflowY = style?.overflowY || '';
  const isScrollable = /(auto|scroll|overlay)/.test(overflowY);
  return isScrollable && element.scrollHeight > element.clientHeight + 1;
};

const getScrollContainers = (element) => {
  if (!element || typeof window === 'undefined') {
    return [];
  }
  const containers = [];
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    if (isScrollableContainer(parent)) {
      containers.push(parent);
    }
    parent = parent.parentElement;
  }
  containers.push(window);
  return containers;
};

export function usePagedGuides({
  contentRef,
  layoutMode = 'strict',
  metrics = REPORT_PAGE_METRICS,
}) {
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  const usablePageHeight = useMemo(
    () => getUsablePageHeight(metrics),
    [metrics]
  );

  useEffect(() => {
    if (!isPagedMode(layoutMode)) {
      setPageCount(1);
      setCurrentPage(1);
      return undefined;
    }

    if (typeof window === 'undefined') {
      setPageCount(1);
      setCurrentPage(1);
      return undefined;
    }

    const contentElement = contentRef?.current;
    if (!contentElement) {
      setPageCount(1);
      setCurrentPage(1);
      return undefined;
    }

    let frameId = null;

    const schedule = (fn) => {
      if (frameId) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        fn();
      });
    };

    const recompute = () => {
      const measuredHeight = getElementContentHeight(contentElement);
      const nextPageCount = getPageCountFromHeight(measuredHeight, metrics);
      setContentHeight((prev) => (prev === measuredHeight ? prev : measuredHeight));
      setPageCount((prev) => (prev === nextPageCount ? prev : nextPageCount));
      setCurrentPage((prev) => {
        const nextPage = getCurrentPageFromViewport(contentElement, metrics, nextPageCount);
        return prev === nextPage ? prev : nextPage;
      });
    };

    schedule(recompute);

    const handleViewportUpdate = () => schedule(recompute);
    const scrollContainers = getScrollContainers(contentElement);
    scrollContainers.forEach((container) => {
      container.addEventListener('scroll', handleViewportUpdate, { passive: true });
    });
    window.addEventListener('resize', handleViewportUpdate);

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => schedule(recompute));
      observer.observe(contentElement);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      scrollContainers.forEach((container) => {
        container.removeEventListener('scroll', handleViewportUpdate);
      });
      window.removeEventListener('resize', handleViewportUpdate);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [contentRef, layoutMode, metrics]);

  const canvasMinHeight = useMemo(() => {
    if (!isPagedMode(layoutMode)) {
      return Math.max(1, contentHeight);
    }
    const interSheetGaps = Math.max(0, pageCount - 1) * metrics.sheetGapPx;
    return Math.max(contentHeight, (pageCount * usablePageHeight) + interSheetGaps);
  }, [contentHeight, layoutMode, metrics.sheetGapPx, pageCount, usablePageHeight]);

  return {
    pageCount,
    currentPage,
    usablePageHeight,
    canvasMinHeight,
    metrics,
  };
}

export default usePagedGuides;
